/**
 * ---------------------------------------------------------------------------------------------------
 * DSPLACE — theme customisations
 * ---------------------------------------------------------------------------------------------------
 *
 * Loaded as a single ES module from layout/theme.liquid. Styles live in assets/dsplace-custom.css.
 *
 * CONTENTS
 *   1. Wishlist            localStorage store + <wishlist-button> + <wishlist-grid>
 *   2. Product carousel    <product-carousel> — swipe/drag through card media, touch hover-reveal
 *   3. Size quick-add      delegated size row + add-to-cart, and the mobile +/- toggle
 *
 * WHY CUSTOM ELEMENTS, NOT DOMContentLoaded
 *   Prestige injects product cards after page load — facet/filter reloads, pagination, the wishlist
 *   grid. The 9.2.1 theme bound its listeners once on DOMContentLoaded, so every one of those injected
 *   cards was dead: no swipe, no size row, no add-to-cart. Custom elements get connectedCallback for
 *   free whenever a card enters the DOM, and the document-level delegation in §3 covers the rest.
 * ---------------------------------------------------------------------------------------------------
 */

const shopifyRoot = () => window.Shopify?.routes?.root ?? '/';

/* ===================================================================================================
   1. WISHLIST

   Storage is localStorage only (key `shopify-wishlist`, a comma-joined list of product handles).
   No app, no customer account, no server persistence — a wishlist does not follow a shopper across
   devices. That was true in 9.2.1 too; it is a deliberate carry-over, not an oversight.
   =================================================================================================== */

const WISHLIST_KEY = 'shopify-wishlist';
const WISHLIST_DELIMITER = ',';

/** @returns {string[]} saved product handles */
export function getWishlist() {
  try {
    const raw = localStorage.getItem(WISHLIST_KEY);
    return raw ? raw.split(WISHLIST_DELIMITER).filter(Boolean) : [];
  } catch {
    // Safari private mode throws on localStorage access
    return [];
  }
}

function setWishlist(handles) {
  try {
    if (handles.length) {
      localStorage.setItem(WISHLIST_KEY, handles.join(WISHLIST_DELIMITER));
    } else {
      localStorage.removeItem(WISHLIST_KEY);
    }
  } catch {
    /* storage unavailable — the in-page state below still updates */
  }

  document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { wishlist: handles } }));
  return handles;
}

export function wishlistContains(handle) {
  return getWishlist().includes(handle);
}

export function toggleWishlist(handle) {
  const wishlist = getWishlist();
  const index = wishlist.indexOf(handle);

  if (index === -1) {
    wishlist.push(handle);
  } else {
    wishlist.splice(index, 1);
  }

  return setWishlist(wishlist);
}

/**
 * The heart toggle on a product card.
 *
 * State is reflected via `aria-pressed`, which doubles as the CSS hook — so there is no separate
 * `active` class that can drift out of sync with what's actually stored.
 */
class WishlistButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.handle = this.getAttribute('handle');

    if (!this.button || !this.handle) return;

    this.sync();
    this.button.addEventListener('click', this.onClick);
    document.addEventListener('wishlist:updated', this.sync);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onClick);
    document.removeEventListener('wishlist:updated', this.sync);
  }

  onClick = (event) => {
    // The whole card figure is clickable — don't navigate when the heart is what was hit.
    event.preventDefault();
    event.stopPropagation();
    toggleWishlist(this.handle);
  };

  sync = () => {
    this.button.setAttribute('aria-pressed', String(wishlistContains(this.handle)));
  };
}

/**
 * The wishlist page grid.
 *
 * Each saved handle is fetched at /products/<handle>?view=card, which Shopify resolves to
 * templates/product.card.json -> sections/product-card-view.liquid (layout: false). The <product-card>
 * element is lifted out of the response. That indirection is what lets the wishlist reuse the real
 * product card instead of duplicating its markup.
 */
class WishlistGrid extends HTMLElement {
  connectedCallback() {
    this.emptyMessage = this.querySelector('[data-wishlist-empty]');
    this.grid = this.querySelector('[data-wishlist-grid]');

    this.render();
    document.addEventListener('wishlist:updated', this.render);
  }

  disconnectedCallback() {
    document.removeEventListener('wishlist:updated', this.render);
  }

  render = async () => {
    const handles = getWishlist();

    if (!handles.length) {
      this.grid.innerHTML = '';
      this.emptyMessage?.removeAttribute('hidden');
      return;
    }

    this.emptyMessage?.setAttribute('hidden', '');

    const cards = await Promise.all(handles.map((handle) => this.fetchCard(handle)));

    // A handle whose product was deleted or unpublished resolves to null — drop it rather than
    // rendering a hole in the grid.
    this.grid.innerHTML = cards.filter(Boolean).join('');
  };

  async fetchCard(handle) {
    try {
      const response = await fetch(`${shopifyRoot()}products/${handle}?view=card`);
      if (!response.ok) return null;

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      return doc.querySelector('product-card')?.outerHTML ?? null;
    } catch {
      return null;
    }
  }
}

/* ===================================================================================================
   2. PRODUCT CARD IMAGE CAROUSEL

   Swipe/drag through a product's media without leaving the grid. Also owns the press-and-hold gesture
   that reveals the second image on touch devices — desktop uses plain CSS :hover (see dsplace-custom.css).
   =================================================================================================== */

const CAROUSEL = {
  dragThreshold: 5, // px of movement before we call it a drag rather than a tap
  snapThreshold: 50, // px of movement before we advance a slide on release
  edgeResistance: 3, // divisor applied to overscroll at the first/last slide
  transitionDuration: 300,
  touchHoverRevealDelay: 80, // short enough to feel instant, long enough for a swipe to cancel it
  touchHoverLockDelay: 200 // once held this long the hover wins and swiping is ignored
};

/** Things that must never trigger card navigation when clicked. */
const INTERACTIVE_SELECTOR = [
  'a',
  'button',
  'input',
  'select',
  'textarea',
  'label',
  'summary',
  '[role="button"]',
  '.product-carousel__dot',
  '.rel-color-swatches',
  'wishlist-button',
  '.size-toggle',
  '.product-sizes li'
].join(', ');

class ProductCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    this.slides = this.track ? Array.from(this.track.children) : [];
    this.dots = Array.from(this.querySelectorAll('[data-slide-index]'));
    this.figure = this.closest('.product-card__figure');
    this.productUrl = this.getAttribute('product-url');
    this.swipeEnabled = this.getAttribute('swipe-enabled') !== 'false';

    if (!this.track || this.slides.length === 0) return;

    // NAMING TRAP — DO NOT rename `trackX` back to `translate`.
    //
    // `translate` is a RESERVED property on HTMLElement: it reflects the global `translate` HTML
    // attribute and is a *boolean* accessor on the prototype. Because this class extends HTMLElement,
    // `this.translate = -1012.5` silently coerces to `true`, and setTransform then emits
    // `translate3d(truepx, 0, 0)` — invalid CSS, which the browser discards without a word. The dots
    // and the drag both appeared completely dead while every line of JS ran perfectly.
    //
    // Same reason to steer clear of: title, lang, dir, hidden, draggable, slot, id, style, dataset.
    this.index = 0;
    this.startX = 0;
    this.trackX = 0;
    this.trackXAtDragStart = 0;
    this.isDragging = false;
    this.hasMoved = false;
    this.isTouchHoverLocked = false;

    this.measure();
    this.bind();
    this.updateDots();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.clearTouchHoverTimers();
    this.resizeObserver?.disconnect();
  }

  bind() {
    // One AbortController tears every listener down in disconnectedCallback, which matters because
    // facet reloads swap whole grids in and out.
    this.controller = new AbortController();
    const { signal } = this.controller;

    // Dots respond to HOVER: sweeping across them scrubs through the product's images, no clicking.
    //
    // Delegated `mouseover` on the container, NOT a `mouseenter` per dot. mouseover bubbles and re-fires
    // on every dot-to-dot transition, so one listener covers the whole row and a fast sweep can't slip
    // between dots. Click stays bound for touch, where no hover exists at all. goToSlide is a no-op when
    // the index is unchanged, so a hybrid device firing both is harmless.
    //
    // Note what is NOT here: hiding the second-image overlay. That is done in CSS, off the browser's own
    // :hover — see dsplace-custom.css. A JS class would have to be added by one event and removed by
    // another, and a single missed event leaves the card stuck.
    this.dotsContainer = this.querySelector('.product-carousel__dots');
    this.dotsContainer?.addEventListener('mouseover', this.onDotHover, { signal });
    this.dotsContainer?.addEventListener('click', this.onDotClick, { signal });

    this.figure?.addEventListener('click', this.onFigureClick, { signal });

    // Clears the drag suppression (see suppressHover).
    this.figure?.addEventListener('mouseleave', this.releaseHover, { signal });

    // Touch press-and-hold to reveal the second image.
    // Deliberately NOT bound to touchcancel: iOS fires it when handing the touch off to native
    // scrolling, which would drop the overlay while the finger is still down.
    this.addEventListener('touchstart', this.onTouchHoverStart, { passive: true, signal });
    this.addEventListener('touchend', this.onTouchHoverEnd, { passive: true, signal });

    if (this.swipeEnabled) {
      this.track.addEventListener('touchstart', this.onDragStart, { passive: true, signal });
      this.track.addEventListener('touchmove', this.onDragMove, { passive: false, signal });
      this.track.addEventListener('touchend', this.onDragEnd, { signal });

      this.track.addEventListener('mousedown', this.onDragStart, { signal });
      this.track.addEventListener('mousemove', this.onDragMove, { signal });
      this.track.addEventListener('mouseup', this.onDragEnd, { signal });
      this.track.addEventListener('mouseleave', this.onDragEnd, { signal });
    }

    this.track.addEventListener('dragstart', (event) => event.preventDefault(), { signal });

    // Slide width is read from layout, so it must be re-read whenever the card resizes. A
    // ResizeObserver beats a window resize listener here, because the grid also reflows when a filter
    // changes the column count without the window ever resizing.
    this.resizeObserver = new ResizeObserver(() => {
      this.measure();
      this.updatePosition(false);
    });
    this.resizeObserver.observe(this);
  }

  measure() {
    this.slideWidth = this.slides[0].getBoundingClientRect().width;
  }

  onDotHover = (event) => {
    const dot = event.target.closest('[data-slide-index]');
    if (dot) this.goToSlide(Number(dot.dataset.slideIndex));
  };

  /** Touch has no hover, so a dot is tapped. Stopped from bubbling, or the figure would navigate. */
  onDotClick = (event) => {
    const dot = event.target.closest('[data-slide-index]');
    if (!dot) return;

    event.preventDefault();
    event.stopPropagation();
    this.goToSlide(Number(dot.dataset.slideIndex));
  };

  /** Clicking anywhere on the card image navigates to the product — except on a real control. */
  onFigureClick = (event) => {
    if (this.isDragging || this.hasMoved) {
      event.preventDefault();
      return;
    }

    if (!this.productUrl || event.defaultPrevented) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (typeof event.button === 'number' && event.button !== 0) return;
    if (this.isInteractive(event.target)) return;

    window.location.href = this.productUrl;
  };

  isInteractive(target) {
    return Boolean(target.closest?.(INTERACTIVE_SELECTOR));
  }

  clearTouchHoverTimers() {
    clearTimeout(this.revealTimer);
    clearTimeout(this.lockTimer);
    this.revealTimer = null;
    this.lockTimer = null;
  }

  /**
   * The hover overlay (second image) is painted ON TOP of the carousel, so while the cursor is inside
   * the card it hides whatever slide is underneath. A drag would therefore move the slides invisibly.
   *
   * DRAG IS THE ONLY THING THAT USES THIS. Hovering the dots is handled entirely in CSS, off the
   * browser's own `:hover` — see `:has(.product-carousel__dots:hover)` in dsplace-custom.css. That was a
   * deliberate move away from doing it here: a class has to be added by one event and removed by
   * another, and any missed event leaves the card stuck showing the wrong layer. Native :hover cannot
   * be missed, and it un-sets itself the moment the cursor leaves the dots — which is exactly what
   * brings the second image back.
   *
   * A drag has no such self-clearing state, so it does need a class. It's dropped on mouseleave
   * (desktop) and on touchend (mobile, which never fires mouseleave at all).
   */
  suppressHover() {
    if (!this.figure) return;

    this.clearTouchHoverTimers();
    this.isTouchHoverLocked = false;
    this.figure.classList.remove('is-touch-hovering');
    this.figure.classList.add('is-hover-suppressed');
  }

  releaseHover = () => {
    this.figure?.classList.remove('is-hover-suppressed');
  };

  onTouchHoverStart = (event) => {
    if (!this.figure || this.isInteractive(event.target)) return;

    this.isTouchHoverLocked = false;
    this.clearTouchHoverTimers();

    this.revealTimer = setTimeout(() => {
      this.figure.classList.add('is-touch-hovering');

      this.lockTimer = setTimeout(() => {
        this.isTouchHoverLocked = true;
      }, CAROUSEL.touchHoverLockDelay);
    }, CAROUSEL.touchHoverRevealDelay);
  };

  onTouchHoverEnd = () => {
    this.clearTouchHoverTimers();
    this.isTouchHoverLocked = false;
    this.figure?.classList.remove('is-touch-hovering');

    // Touch never fires mouseleave. Without this, one swipe would leave `is-hover-suppressed` stuck on
    // the card and press-and-hold would be dead on it for the rest of the session.
    this.releaseHover();
  };

  onDragStart = (event) => {
    this.isDragging = true;
    this.hasMoved = false;
    this.startX = this.positionX(event);
    this.trackXAtDragStart = this.trackX;
    this.track.style.transition = 'none';
  };

  onDragMove = (event) => {
    if (!this.isDragging) return;

    // Past the hold threshold the hover overlay owns the gesture; ignore the swipe until release.
    if (this.isTouchHoverLocked) return;

    const deltaX = this.positionX(event) - this.startX;

    if (Math.abs(deltaX) > CAROUSEL.dragThreshold) {
      // The user is swiping, not holding. Drop the overlay on the FIRST frame of the drag rather than
      // on release, so the slides are visible while they're being dragged; suppressHover also kills the
      // pending press-and-hold reveal and hides it if it already fired.
      if (!this.hasMoved) this.suppressHover();

      this.hasMoved = true;

      if (event.cancelable && event.type === 'touchmove') {
        event.preventDefault();
      }
    }

    const atStart = this.index === 0 && deltaX > 0;
    const atEnd = this.index === this.slides.length - 1 && deltaX < 0;
    const resistance = atStart || atEnd ? CAROUSEL.edgeResistance : 1;

    this.trackX = this.trackXAtDragStart + deltaX / resistance;
    this.setTransform();
  };

  onDragEnd = () => {
    if (!this.isDragging) return;

    this.isDragging = false;

    const movedBy = this.trackX - this.trackXAtDragStart;

    if (movedBy < -CAROUSEL.snapThreshold && this.index < this.slides.length - 1) {
      this.index++;
    } else if (movedBy > CAROUSEL.snapThreshold && this.index > 0) {
      this.index--;
    }

    this.updatePosition(true);
    this.updateDots();

    // Keep hasMoved true just past the click event, so releasing a drag doesn't navigate.
    setTimeout(() => {
      this.hasMoved = false;
    }, 50);
  };

  goToSlide(index) {
    if (index < 0 || index >= this.slides.length) return;

    // mouseover fires continuously while the cursor sits on a dot — don't restart the animation on
    // every one of them. This guard is what makes hover-scrubbing and tap-to-slide safe to co-exist.
    if (index === this.index) return;

    if (!this.slideWidth) this.measure();

    this.index = index;
    this.updatePosition(true);
    this.updateDots();
  }

  updatePosition(animate = true) {
    this.trackX = -this.index * this.slideWidth;
    this.track.style.transition = animate
      ? `transform ${CAROUSEL.transitionDuration}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`
      : 'none';
    this.setTransform();
  }

  setTransform() {
    // A non-finite value here produces invalid CSS, which the browser drops in silence — the exact
    // failure mode the `translate` name collision caused. Fail loudly instead of freezing the carousel.
    if (!Number.isFinite(this.trackX)) {
      console.error('[DSPLACE] carousel offset is not a number:', this.trackX);
      return;
    }

    this.track.style.transform = `translate3d(${this.trackX}px, 0, 0)`;
  }

  updateDots() {
    this.dots.forEach((dot, index) => {
      const isActive = index === this.index;
      dot.classList.toggle('active', isActive);
      dot.setAttribute('aria-current', String(isActive));
    });
  }

  positionX(event) {
    return event.type.includes('mouse') ? event.pageX : event.touches[0].clientX;
  }
}

/* ===================================================================================================
   3. INLINE SIZE QUICK-ADD

   Single-option (size-only) products expose their variants on the card. Desktop reveals the row on
   card hover (pure CSS); mobile collapses it behind a +/- toggle, handled here.

   Delegated at the document level, so cards injected later (filters, pagination, wishlist grid) work
   with no re-init. The 9.2.1 version also called `product.querySelector('.hide_show').addEventListener`
   unguarded, which threw a TypeError on every multi-option product — they have no toggle button.
   =================================================================================================== */

async function addVariantToCart(item) {
  const variantId = item.dataset.variant;

  if (!variantId || item.dataset.adding === 'true') return;

  item.dataset.adding = 'true';

  try {
    const response = await fetch(`${shopifyRoot()}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    });

    if (!response.ok) throw new Error(`Add to cart failed: ${response.status}`);

    item.classList.add('added-to-cart');

    // Prestige listens for this to re-pull the cart and open the drawer.
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  } catch (error) {
    console.error('[DSPLACE] Could not add variant to cart', error);
  } finally {
    delete item.dataset.adding;
  }
}

function toggleSizes(toggle) {
  const card = toggle.closest('product-card');
  if (!card) return;

  const willOpen = !card.classList.contains('sizes-open');

  // Only one card's sizes open at a time.
  document.querySelectorAll('product-card.sizes-open').forEach((other) => {
    other.classList.remove('sizes-open');
    other.querySelector('.size-toggle')?.setAttribute('aria-expanded', 'false');
  });

  card.classList.toggle('sizes-open', willOpen);
  toggle.setAttribute('aria-expanded', String(willOpen));
}

document.addEventListener('click', (event) => {
  const toggle = event.target.closest('.size-toggle');

  if (toggle) {
    event.preventDefault();
    event.stopPropagation();
    toggleSizes(toggle);
    return;
  }

  const sizeItem = event.target.closest('.product-sizes li.has-stock');

  if (sizeItem) {
    event.preventDefault();
    event.stopPropagation();
    addVariantToCart(sizeItem);
  }
});

/* =================================================================================================== */

customElements.define('wishlist-button', WishlistButton);
customElements.define('wishlist-grid', WishlistGrid);
customElements.define('product-carousel', ProductCarousel);
